import { useMutation } from '@apollo/client';
import { Button, Modal, Text, Textarea } from '@nextui-org/react';
import { useTheme } from 'next-themes';
import { Dispatch, SetStateAction, useState } from 'react';
import { Check } from 'react-feather';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';
import { gql } from '../generated';
import useAuth from '../hooks/useAuth';
import { HadithModalData } from '../pages/chapter/[bookId]/[sectionId]/[chapterNumber]';
import { toastStyle } from './HadithCard';

export const REPORT_HADITH_MUTATION = gql(`
  mutation reportHadith($description: String!, $identifier: String!, $email: String) {
    addReport(description: $description, identifier: $identifier, email: $email)
  }
`);

export default function ReportHadithModal({
  visible,
  setReportHadithModalVisible,
  chapterNumber,
  bookName,
  setHadithModalData,
  hadithModalData,
  bookNumber,
  sectionNumber,
  userEmail,
}: {
  visible: boolean;
  setReportHadithModalVisible?: any;
  chapterNumber: string;
  bookName: string;
  bookNumber: string;
  sectionNumber: string;
  hadithModalData: HadithModalData;
  setHadithModalData: Dispatch<SetStateAction<HadithModalData>>;
  userEmail?: string;
}) {
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [description, setDescription] = useState('');
  const [errMessage, setErrMessage] = useState<String | null>(null);
  const [reporterEmail, setReporterEmail] = useState<string>('');
  const { resolvedTheme } = useTheme();
  const user = useAuth();

  const reportedHadithToast = (
    <div>
      <Check className="inline mr-2" />
      Hadith reported successfully
    </div>
  );

  const [reportHadith] = useMutation(REPORT_HADITH_MUTATION, {
    onCompleted: (data) => {
      console.log(data);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleSubmit = async () => {
    if (!recaptchaValue) {
      setErrMessage('Please verify that you are not a robot');
      return;
    }

    reportHadith({
      variables: {
        description,
        identifier: `${bookNumber}/${sectionNumber}/${chapterNumber}/${hadithModalData.hadithNumber}`,
        email: reporterEmail,
      },
      onCompleted: (data) => {
        console.log(data);
        setHadithModalData({
          visible: false,
        });
        toast(reportedHadithToast, { style: toastStyle(resolvedTheme === 'dark'), hideProgressBar: true });
      },
    });
  };

  return (
    <Modal
      closeButton
      aria-labelledby="modal-title"
      open={visible}
      onClose={() =>
        setHadithModalData({
          visible: false,
        })
      }
    >
      <Modal.Header>
        <Text id="modal-title" size={18}>
          Report an issue with this Hadith
        </Text>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col text-center">
          <Text className="font-bold" size={18}>
            {bookName ?? 'N/A'}
          </Text>
          <Text size={18}>Chapter #{chapterNumber ?? 'N/A'}</Text>
          <Text size={18}>Hadith #{hadithModalData.hadithNumber ?? 'N/A'}</Text>
        </div>
        <Textarea
          bordered
          fullWidth
          label="Please describe the problem and/or solution. If you have a reference, please include it."
          placeholder="Enter details"
          onChange={(e) => {
            setDescription(e.target.value);
          }}
        />
        <Textarea
          key={userEmail}
          bordered
          fullWidth
          defaultValue={userEmail}
          id="report-user-email"
          label="Enter your email to be notified of an update"
          maxRows={1}
          placeholder="Email"
          onChange={(e) => setReporterEmail(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        {errMessage && (
          <p className="text-center w-full text-red-500" color="error">
            {errMessage}
          </p>
        )}
        <ReCAPTCHA
          className="m-auto"
          sitekey="6LfowlklAAAAALZSHupaxgGkDDDW-ufDx__HgBb4"
          onChange={(value: any) => {
            setRecaptchaValue(value);
            setErrMessage(null);
          }}
        />
        <Button
          auto
          flat
          color="error"
          onPress={() =>
            setHadithModalData({
              visible: false,
            })
          }
        >
          Cancel
        </Button>
        <Button auto flat color="default" onPress={handleSubmit}>
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
